import { useEffect, useRef, useState } from "react";
import { Row, Table, Container, Button, Col, Form, InputGroup } from "react-bootstrap";
import { useSelector } from "react-redux";
import EditModal from "./EditModal";
import RemoveModal from "./RemoveModal";
import MessageModal from "../_components/MessageModal";
import toastService from "../../_services/toastService";
import SatAPI from "../../_services/sat";
import { sortProgrammesLikeOpleidingenPage, isShownInOpleidingenFilter } from "../../_helpers/sortProgrammes";

const api = SatAPI();
// localStorage keys for restoring selected snapshot and diff panel open state.
const TASK_DIFF_SNAPSHOT_STORAGE_KEY = "SAT-TaskDiff-SnapshotName";
const TASK_DIFF_OPEN_STORAGE_KEY = "SAT-TaskDiff-Open";

function Tasks() {
  const snapshot = useSelector(state => state.snapshot.fileName);

  const [tasks, setTasks] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [diffSnapshotName, setDiffSnapshotName] = useState("");
  const [runningDiff, setRunningDiff] = useState(false);
  const [taskDiffData, setTaskDiffData] = useState({ taskDiff: null, currentTasks: [], snapshotTasks: [] });
  const [isDiffOpen, setIsDiffOpen] = useState(false);
  const [availableSnapshots, setAvailableSnapshots] = useState([]);
  const [highlightedTaskID, setHighlightedTaskID] = useState(null);
  const taskRowRefs = useRef({});
  const diffDetailsContainerRef = useRef(null);

  // filter
  const [selectedProgrammeIDs, setSelectedProgrammeIDs] = useState([]);

  const loadTasks = async (showPageLoading = true) => {
    try {
      if (showPageLoading) {
        setLoading(true);
      }
      const response = await api.getProgrammes(snapshot);
      setProgrammes(sortProgrammesLikeOpleidingenPage(response, isShownInOpleidingenFilter));

      const taskResponse = await api.getTasks(snapshot);
      taskResponse.sort((a, b) => b.name < a.name);
      setTasks(taskResponse);

      // check if SAT-PersonalProgrammeFilter exists in local storage - if not: create it to avoid null reference
      if (!localStorage.getItem('SAT-PersonalProgrammeFilter')) {
        localStorage.setItem('SAT-PersonalProgrammeFilter', JSON.stringify([]));
      }

      let personalProgrammeIDs = [];
      JSON.parse(localStorage.getItem('SAT-PersonalProgrammeFilter')).forEach(selectedProgramme => {
        personalProgrammeIDs = [...personalProgrammeIDs, selectedProgramme.ID];
      });
      setSelectedProgrammeIDs(personalProgrammeIDs);

      if (showPageLoading) {
        setLoading(false);
      }
      return true;
    } catch (error) {
      toastService.send({ title: "An error occured", message: error.toString() });
      if (showPageLoading) {
        setLoading(false);
      }
      return false;
    }
  };

  const openEditModal = data => {
    setEditModal(
      <EditModal
        // After edit, refresh base list and diff so comparison stays in sync.
        hideModal={async () => {
          setEditModal(null);
          await loadTasks();
          await refreshDiffAfterDataChange();
        }}
        data={data}
      />,
    );
  };

  const openRemoveModal = task => {
    setRemoveModal(
      <RemoveModal
        // After delete, refresh base list and diff so comparison stays in sync.
        hideModal={async () => {
          setRemoveModal(null);
          await loadTasks();
          await refreshDiffAfterDataChange();
        }}
        data={task}
      />,
    );
  };

  useEffect(() => {
    // Restore persisted diff state and pre-load data for current snapshot context.
    const savedSnapshotName = localStorage.getItem(TASK_DIFF_SNAPSHOT_STORAGE_KEY) || "";
    const savedOpenState = localStorage.getItem(TASK_DIFF_OPEN_STORAGE_KEY);
    const shouldOpenDiff = savedOpenState === "true";

    setDiffSnapshotName(savedSnapshotName);
    setIsDiffOpen(shouldOpenDiff);

    const initializePage = async () => {
      setLoading(true);
      const [loaded] = await Promise.all([
        loadTasks(false),
        loadSnapshots(),
      ]);

      if (loaded && savedSnapshotName.trim()) {
        try {
          if (await snapshotExists(savedSnapshotName.trim())) {
            await runDiffWithSnapshot(savedSnapshotName.trim(), false, false);
          } else {
            localStorage.removeItem(TASK_DIFF_SNAPSHOT_STORAGE_KEY);
            localStorage.setItem(TASK_DIFF_OPEN_STORAGE_KEY, "false");
            setDiffSnapshotName("");
            setIsDiffOpen(false);
          }
        } catch {
          // Keep the page usable even when snapshot validation fails.
        }
      }

      setLoading(false);
    };

    initializePage();
  }, [snapshot]);

  const toggleSelectedProgrammeFiler = (id, checked) => {
    if (checked) {
      setSelectedProgrammeIDs([...selectedProgrammeIDs, id]);
    } else {
      setSelectedProgrammeIDs(selectedProgrammeIDs.filter(i => i !== id));
    }
  };

  const loadSnapshots = async () => {
    // Build a clean, unique, sorted list of snapshot names for input suggestions and selector.
    try {
      const snapshots = await api.getSnapshots();
      const snapshotNames = [...new Set(
        (snapshots || [])
          .map(s => (s.fileName || s.name || "").trim())
          .filter(Boolean),
      )].sort((a, b) => b.localeCompare(a));

      setAvailableSnapshots(snapshotNames);
      return snapshotNames;
    } catch {
      setAvailableSnapshots([]);
      return [];
    }
  };

  const snapshotExists = async snapshotName => {
    // Validate snapshot names against cached list, with lazy API fallback.
    const snapshots = availableSnapshots.length > 0 ? availableSnapshots : await loadSnapshots();
    return snapshots.includes(snapshotName);
  };

  const formatApiError = async error => {
    if (error instanceof Response) {
      const body = await error.text();
      return body || `Request failed with status ${error.status}`;
    }

    return error?.message || String(error);
  };

  const runDiffWithSnapshot = async (selectedSnapshot, showErrorToast = true, showLoadingIndicator = true) => {
    // Fetch diff + both task sources in parallel; backend computes the actual comparison.
    if (showLoadingIndicator) {
      setRunningDiff(true);
    }

    try {
      const [diffResult, currentTasksResponse, snapshotTasksResponse] = await Promise.allSettled([
        api.getTasksDiff(selectedSnapshot),
        api.getTasks(null),
        api.getTasks(selectedSnapshot),
      ]);

      if (diffResult.status !== "fulfilled") {
        throw diffResult.reason;
      }

      const result = diffResult.value;
      setTaskDiffData({
        taskDiff: result,
        currentTasks: currentTasksResponse.status === "fulfilled" ? currentTasksResponse.value || [] : [],
        snapshotTasks: snapshotTasksResponse.status === "fulfilled" ? snapshotTasksResponse.value || [] : [],
      });

      localStorage.setItem(TASK_DIFF_SNAPSHOT_STORAGE_KEY, selectedSnapshot);
    } catch (error) {
      if (showErrorToast) {
        setTaskDiffData({ taskDiff: null, currentTasks: [], snapshotTasks: [] });
        toastService.send({ title: "Vergelijking mislukt", message: await formatApiError(error) });
      }
    } finally {
      if (showLoadingIndicator) {
        setRunningDiff(false);
      }
    }
  };

  const runDiff = async () => {
    // User-triggered compare flow: validate input/snapshot and then open the diff panel.
    const selectedSnapshot = diffSnapshotName.trim();

    if (!selectedSnapshot) {
      toastService.send({ title: "Snapshot vereist", message: "Vul een snapshotnaam in." });
      return;
    }

    try {
      const exists = await snapshotExists(selectedSnapshot);
      if (!exists) {
        setTaskDiffData({ taskDiff: null, currentTasks: [], snapshotTasks: [] });
        setIsDiffOpen(false);
        localStorage.setItem(TASK_DIFF_OPEN_STORAGE_KEY, "false");
        toastService.send({ title: "Snapshot bestaat niet", message: "De ingevulde snapshot bestaat niet." });
        return;
      }
    } catch (error) {
      toastService.send({ title: "Snapshotcontrole mislukt", message: error.toString() });
      return;
    }

    await runDiffWithSnapshot(selectedSnapshot, true, true);
    setIsDiffOpen(true);
    localStorage.setItem(TASK_DIFF_OPEN_STORAGE_KEY, "true");
  };

  const refreshDiffAfterDataChange = async () => {
    // Silent diff refresh after mutations, only when a comparison is already active.
    const selectedSnapshot = diffSnapshotName.trim();
    if (!selectedSnapshot || !taskDiffData.taskDiff) {
      return;
    }

    await runDiffWithSnapshot(selectedSnapshot, false, false);
  };

  const collapseAllDiffDetails = () => {
    // Collapse all expanded details elements in the diff section.
    const container = diffDetailsContainerRef.current;
    if (!container) {
      return;
    }

    container.querySelectorAll("details[open]").forEach(detail => detail.removeAttribute("open"));
  };

  const jumpToTaskRow = taskID => {
    // Scroll to and temporarily highlight the related task row in the main table.
    const normalizedTaskID = `${taskID}`;
    const row = taskRowRefs.current[normalizedTaskID];

    if (!row) {
      toastService.send({ title: "Taak niet zichtbaar", message: "De rij van deze taak werd niet gevonden in de huidige tabelweergave." });
      return;
    }

    if (row.classList.contains("d-none")) {
      toastService.send({ title: "Taak gefilterd", message: "De taak is momenteel weggefilterd. Pas eerst de filters aan." });
      return;
    }

    row.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedTaskID(normalizedTaskID);
    setTimeout(() => setHighlightedTaskID(null), 1800);
  };

  const openEditModalFromDiff = taskID => {
    // Open the regular edit modal from a diff item using its task id.
    const task = tasks.find(t => (t.ID ?? t.id) === taskID);
    if (!task) {
      toastService.send({ title: "Taak niet gevonden", message: "Deze taak kon niet geopend worden voor bewerken." });
      return;
    }

    openEditModal(task);
  };

  const FIELD_NAME_ALIASES = {
    // Canonical field names so snake_case/camelCase variants are handled consistently.
    categoryID: "categoryID",
    categoryId: "categoryID",
    category_id: "categoryID",
    has_forfait: "hasForfait",
    hasForfait: "hasForfait",
    educational_activity: "educationalActivity",
    educationalActivity: "educationalActivity",
  };

  const FIELD_LABELS = {
    // Front-end labels used in diff summaries and field rows.
    name: "Naam",
    percentage: "Percentage",
    categoryID: "Categorie",
    comment: "Commentaar",
    hasForfait: "Forfait",
    educationalActivity: "Onderwijsactiviteit",
    programmes: "Opleidingen",
    labels: "Labels",
  };

  const normalizeFieldName = rawFieldName => FIELD_NAME_ALIASES[rawFieldName] || rawFieldName;

  const getCategoryID = task => task?.categoryID ?? task?.categoryId ?? task?.category?.ID ?? 0;

  const buildTaskPayloadFromTask = task => ({
    // Rebuild API payload from a task object before full/partial restore operations.
    name: task?.name ?? "",
    programmes: task?.programmes || [],
    comment: task?.comment ?? "",
    percentage: task?.percentage ?? 0,
    hasForfait: task?.hasForfait ?? task?.has_forfait ?? true,
    educationalActivity: task?.educationalActivity ?? task?.educational_activity ?? false,
    categoryID: getCategoryID(task),
    labels: task?.labels || [],
  });

  const validateCategoryForRestore = async (categoryID, categoryName = "") => {
    const normalizedCategoryID = Number(categoryID);

    // categoryID = 0 means "geen categorie" and is always accepted.
    if (!normalizedCategoryID) {
      return true;
    }

    const categories = await api.getTaskCategories(null);
    const categoryExists = (categories || []).some(category => category?.ID === normalizedCategoryID);

    if (!categoryExists) {
      const categoryLabel = categoryName || `ID ${normalizedCategoryID}`;
      toastService.send({
        title: "Herstellen mislukt",
        message: `De categorie (${categoryLabel}) bestaat niet meer. Hermaak eerst deze categorie of herstel de taak zonder categorie.`,
      });
      return false;
    }

    return true;
  };

  const applySnapshotFieldToPayload = (payload, snapshotTask, rawFieldName) => {
    // Apply a single field from snapshot to an existing payload for field-level restore.
    const fieldName = normalizeFieldName(rawFieldName);

    switch (fieldName) {
      case "name":
        payload.name = snapshotTask?.name ?? "";
        break;
      case "percentage":
        payload.percentage = snapshotTask?.percentage ?? 0;
        break;
      case "comment":
        payload.comment = snapshotTask?.comment ?? "";
        break;
      case "hasForfait":
        payload.hasForfait = snapshotTask?.hasForfait ?? snapshotTask?.has_forfait ?? true;
        break;
      case "educationalActivity":
        payload.educationalActivity = snapshotTask?.educationalActivity ?? snapshotTask?.educational_activity ?? false;
        break;
      case "categoryID":
        payload.categoryID = getCategoryID(snapshotTask);
        break;
      case "programmes":
        payload.programmes = snapshotTask?.programmes || [];
        break;
      case "labels":
        payload.labels = snapshotTask?.labels || [];
        break;
      default:
        break;
    }
  };

  const restoreTaskFromSnapshot = taskID => {
    // Replace an entire task with snapshot values.
    const snapshotTask = getTaskById(taskID, snapshotTasksForDiff);

    if (!snapshotTask) {
      toastService.send({ title: "Snapshot taak niet gevonden", message: "De taak kon niet uit de snapshot worden geladen." });
      return;
    }

    const executeRestore = async () => {
      setConfirmModal(null);
      try {
        const payload = buildTaskPayloadFromTask(snapshotTask);
        const canRestoreWithCategory = await validateCategoryForRestore(payload.categoryID, snapshotTask?.category?.name);
        if (!canRestoreWithCategory) {
          return;
        }

        await api.updateTask(taskID, payload);
        toastService.send({ title: "Hersteld", message: "Taak werd hersteld naar de snapshotversie." });

        await loadTasks();
        await refreshDiffAfterDataChange();
        jumpToTaskRow(taskID);
      } catch (error) {
        toastService.send({ title: "Herstellen mislukt", message: error.toString() });
      }
    };

    setConfirmModal(
      <MessageModal
        title="Taak herstellen"
        message={
          <>
            <span>Ben je zeker dat je deze taak wil herstellen naar de snapshotversie?</span>
            <span className="d-flex gap-2 justify-content-end mt-3">
              <Button variant="secondary" onClick={() => setConfirmModal(null)}>Annuleren</Button>
              <Button variant="warning" onClick={executeRestore}>Herstellen</Button>
            </span>
          </>
        }
        icon="fas fa-rotate-left"
        hideModal={() => setConfirmModal(null)}
      />,
    );
  };

  const restoreTaskFieldFromSnapshot = (taskID, rawFieldName) => {
    // Restore only one changed field while preserving current values for the rest.
    const currentTask = getTaskById(taskID, currentTasksForDiff) || tasks.find(t => (t.ID ?? t.id) === taskID);
    const snapshotTask = getTaskById(taskID, snapshotTasksForDiff);

    if (!currentTask || !snapshotTask) {
      toastService.send({ title: "Herstellen mislukt", message: "Onvoldoende gegevens om dit veld te herstellen." });
      return;
    }

    const fieldLabel = mapFieldNameToFrontendLabel(rawFieldName);

    const executeFieldRestore = async () => {
      setConfirmModal(null);
      try {
        const payload = buildTaskPayloadFromTask(currentTask);
        applySnapshotFieldToPayload(payload, snapshotTask, rawFieldName);

        const categoryName = rawFieldName === "categoryID" || rawFieldName === "categoryId" || rawFieldName === "category_id"
          ? snapshotTask?.category?.name
          : "";
        const canRestoreWithCategory = await validateCategoryForRestore(payload.categoryID, categoryName);
        if (!canRestoreWithCategory) {
          return;
        }

        await api.updateTask(taskID, payload);
        toastService.send({ title: "Veld hersteld", message: `${fieldLabel} werd hersteld naar snapshot.` });

        await loadTasks();
        await refreshDiffAfterDataChange();
        jumpToTaskRow(taskID);
      } catch (error) {
        toastService.send({ title: "Herstellen mislukt", message: error.toString() });
      }
    };

    setConfirmModal(
      <MessageModal
        title="Veld herstellen"
        message={
          <>
            <span>Ben je zeker dat je enkel het veld <strong>{fieldLabel}</strong> wil herstellen naar snapshot?</span>
            <span className="d-flex gap-2 justify-content-end mt-3">
              <Button variant="secondary" onClick={() => setConfirmModal(null)}>Annuleren</Button>
              <Button variant="warning" onClick={executeFieldRestore}>Herstellen</Button>
            </span>
          </>
        }
        icon="fas fa-rotate-left"
        hideModal={() => setConfirmModal(null)}
      />,
    );
  };

  if (loading) {
    return (
      <div className="text-center">
        <i className="fad fa-spinner-third fa-spin fa-5x" />
      </div>
    );
  }

  const getTaskNameById = (taskID, sourceTasks) => {
    const task = sourceTasks.find(t => (t.ID ?? t.id) === taskID);
    return task?.name || `Taak ${taskID}`;
  };

  const getTaskById = (taskID, sourceTasks) => sourceTasks.find(t => (t.ID ?? t.id) === taskID);

  const mapFieldNameToFrontendLabel = fieldName => {
    const normalizedFieldName = normalizeFieldName(fieldName);
    return FIELD_LABELS[normalizedFieldName] || normalizedFieldName;
  };

  const formatText = value => {
    if (value === null || value === undefined || value === "") {
      return "(leeg)";
    }

    return `${value}`;
  };

  const formatAnyValue = value => {
    if (value === null || value === undefined || value === "") {
      return "(leeg)";
    }

    if (Array.isArray(value)) {
      return value
        .map(item => formatAnyValue(item))
        .filter(item => item && item !== "(leeg)")
        .join(", ") || "(leeg)";
    }

    if (typeof value === "object") {
      return value.name
        || value.label?.name
        || value.naam
        || value.code
        || value.ID
        || value.id
        || JSON.stringify(value);
    }

    return `${value}`;
  };

  const formatBool = value => {
    if (value === true) {
      return "Ja";
    }
    if (value === false) {
      return "Nee";
    }
    return "-";
  };

  const getProgrammeNames = task => {
    return (task?.programmes || [])
      .map(p => p?.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .join(", ") || "-";
  };

  const getLabelNames = task => {
    const labels = task?.labels || task?.taskLabels || [];

    return labels
      .map(label => label?.name || label?.label?.name || label?.labelName || label?.label?.labelName || label?.label?.code || label?.code)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .join(", ") || "-";
  };

  const getFieldValueForDisplay = (task, rawFieldName) => {
    // Convert raw field values to readable output used in old/new diff rendering.
    if (!task) {
      return "-";
    }

    const fieldName = normalizeFieldName(rawFieldName);

    switch (fieldName) {
      case "name":
        return formatText(task.name);
      case "percentage":
        return task.percentage ?? "-";
      case "categoryID":
        return formatText(task.category?.name);
      case "comment":
        return formatText(task.comment);
      case "hasForfait":
        return formatBool(task.hasForfait ?? task.has_forfait);
      case "educationalActivity":
        return formatBool(task.educationalActivity ?? task.educational_activity);
      case "programmes":
        return getProgrammeNames(task);
      case "labels":
        return getLabelNames(task);
      default:
        return formatAnyValue(task[fieldName]);
    }
  };

  const taskDiff = taskDiffData.taskDiff;
  const currentTasksForDiff = taskDiffData.currentTasks;
  const snapshotTasksForDiff = taskDiffData.snapshotTasks;

  const createdTasksForDisplay = (taskDiff?.created || [])
    .map(taskID => ({
      taskID,
      taskName: getTaskNameById(taskID, currentTasksForDiff),
    }))
    .sort((a, b) => a.taskName.localeCompare(b.taskName));
  const deletedTaskNames = taskDiff?.deleted?.map(id => getTaskNameById(id, snapshotTasksForDiff)) || [];

  const updatedTasksForDisplay = (taskDiff?.updated || [])
    // Transform backend diff output into sorted, display-ready task and field rows.
    .map(update => {
      const taskID = update.taskID ?? update.taskId;
      const rawChangedFields = [...new Set(update.fieldsChanged || [])]
        .sort((a, b) => mapFieldNameToFrontendLabel(a).localeCompare(mapFieldNameToFrontendLabel(b)));

      const currentTask = getTaskById(taskID, currentTasksForDiff);
      const snapshotTask = getTaskById(taskID, snapshotTasksForDiff);

      const fieldChanges = rawChangedFields.map(rawFieldName => ({
        rawFieldName,
        label: mapFieldNameToFrontendLabel(rawFieldName),
        oldValue: getFieldValueForDisplay(snapshotTask, rawFieldName),
        newValue: getFieldValueForDisplay(currentTask, rawFieldName),
      }));

      const summaryText = rawChangedFields.map(mapFieldNameToFrontendLabel).join(", ");

      return {
        taskID,
        taskName: getTaskNameById(taskID, currentTasksForDiff) || getTaskNameById(taskID, snapshotTasksForDiff),
        summaryText,
        fieldChanges,
      };
    })
    .sort((a, b) => a.taskName.localeCompare(b.taskName));

  return (
    <Container>
      {editModal}
      {removeModal}
      {confirmModal}
      <Row>
        <h1>Taken</h1>
      </Row>
      <Row className="my-2">
        <Col className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
          {/* Combined snapshot picker: typed search + select dropdown + manual compare action. */}
          <InputGroup style={{ maxWidth: "360px" }}>
            <Form.Control
              type="text"
              list={diffSnapshotName ? "task-diff-snapshot-options" : undefined}
              value={diffSnapshotName}
              onChange={e => {
                const newValue = e.target.value;
                setDiffSnapshotName(newValue);
                localStorage.setItem(TASK_DIFF_SNAPSHOT_STORAGE_KEY, newValue);
              }}
              placeholder="Zoek snapshot"
              autoComplete="off"
            />
            <datalist id="task-diff-snapshot-options">
              {availableSnapshots.map(snapshotName => (
                <option key={snapshotName} value={snapshotName} />
              ))}
            </datalist>
            <Form.Select
              aria-label="Selecteer snapshot"
              value={diffSnapshotName}
              onChange={async e => {
                const selectedValue = e.target.value;

                setDiffSnapshotName(selectedValue);
                localStorage.setItem(TASK_DIFF_SNAPSHOT_STORAGE_KEY, selectedValue);

                await runDiffWithSnapshot(selectedValue, true, true);
                setIsDiffOpen(true);
                localStorage.setItem(TASK_DIFF_OPEN_STORAGE_KEY, "true");
              }}
              className="flex-grow-0"
              style={{ width: "44px", paddingInline: "0.4rem", backgroundPosition: "center", textIndent: "-9999px", overflow: "hidden" }}
            >
              {availableSnapshots.map(snapshotName => (
                <option key={snapshotName} value={snapshotName}>{snapshotName}</option>
              ))}
            </Form.Select>
          </InputGroup>
          <Button variant="primary" onClick={runDiff} disabled={runningDiff}>
            {runningDiff ? (
              <>
                <i className="fad fa-spinner-third fa-spin" />
                &nbsp;Vergelijken...
              </>
            ) : (
              <>
                <i className="far fa-code-compare" />
                &nbsp;Vergelijk
              </>
            )}
          </Button>
          <Button variant="success" onClick={() => openEditModal()} disabled={snapshot}>
            <i className="far fa-plus" />
            &nbsp;Nieuw
          </Button>
        </Col>
      </Row>
      {taskDiff && isDiffOpen && (
        <Row className="mb-3">
          <Col>
            {/* Diff panel with summary, per-task details, and restore actions. */}
            <div className="border rounded p-3 bg-light">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <h5 className="mb-0">Aanpassingen</h5>
                <div className="d-flex gap-3">
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={collapseAllDiffDetails}
                  >
                    Alles inklappen
                  </Button>
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={() => {
                      setIsDiffOpen(false);
                      localStorage.setItem(TASK_DIFF_OPEN_STORAGE_KEY, "false");
                    }}
                  >
                    Sluiten
                  </Button>
                </div>
              </div>
              <p className="mb-2">
                <strong>Aangemaakt:</strong> {taskDiff.summary?.created ?? 0} | <strong>Aangepast:</strong>{" "}
                {taskDiff.summary?.updated ?? 0} | <strong>Verwijderd:</strong> {taskDiff.summary?.deleted ?? 0}
              </p>
              <p className="mb-1">
                <strong>Aangemaakt:</strong>{" "}
                {createdTasksForDisplay.length > 0 ? (
                  createdTasksForDisplay.map((createdTask, index) => (
                    <span key={`created-${createdTask.taskID}`}>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 align-baseline text-decoration-none"
                        onClick={() => jumpToTaskRow(createdTask.taskID)}
                      >
                        {createdTask.taskName}
                      </Button>
                      {index < createdTasksForDisplay.length - 1 ? ", " : ""}
                    </span>
                  ))
                ) : "-"}
              </p>
              <p className="mb-1">
                <strong>Verwijderd:</strong>{" "}
                {deletedTaskNames.length > 0 ? deletedTaskNames.join(", ") : "-"}
              </p>
              <div>
                <strong>Aangepast:</strong>
                {updatedTasksForDisplay.length > 0 ? (
                  <ul className="mb-0 mt-2 list-unstyled" ref={diffDetailsContainerRef}>
                    {updatedTasksForDisplay.map(update => (
                      <li key={update.taskID} className="mb-2">
                        <details className="border rounded bg-white px-2 py-1">
                          <summary className="fw-semibold">
                            {update.taskName}
                            <span className="text-muted fw-normal">: {update.summaryText}</span>
                          </summary>
                          <div className="mt-2 d-flex gap-3">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={() => jumpToTaskRow(update.taskID)}
                            >
                              Ga naar rij
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={() => openEditModalFromDiff(update.taskID)}
                            >
                              Open bewerken
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={() => restoreTaskFromSnapshot(update.taskID)}
                              disabled={snapshot}
                            >
                              Herstel naar snapshot
                            </Button>
                          </div>
                          <ul className="mt-2 mb-1 list-unstyled small">
                            {update.fieldChanges.map(change => (
                              <li key={`${update.taskID}-${change.label}`} className="d-flex py-1 border-top">
                                <span className="fw-semibold" style={{ minWidth: "150px" }}>{change.label}</span>
                                <span className="text-muted" style={{ minWidth: "220px" }}>{change.oldValue}</span>
                                <span className="px-2">{">"}</span>
                                <span className="me-3">{change.newValue}</span>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-decoration-none"
                                  onClick={() => restoreTaskFieldFromSnapshot(update.taskID, change.rawFieldName)}
                                  disabled={snapshot}
                                >
                                  Herstel
                                </Button>
                              </li>
                            ))}
                          </ul>
                        </details>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span>&nbsp;-</span>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <Col md={2}>
          <h3>Filter</h3>
          <hr />
          <h4>Opleiding</h4>
          <ul className="list-group">
            {programmes.map(programme => (
              <li key={programme.ID} className="list-group-item text-break">
                <Form.Check
                  inline
                  label={programme.name}
                  id={programme.ID}
                  type="checkbox"
                  checked={selectedProgrammeIDs.includes(programme.ID)}
                  onChange={e => toggleSelectedProgrammeFiler(programme.ID, e.target.checked)}
                />
              </li>
            ))}
          </ul>
        </Col>
        <Col md={10}>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Naam</th>
                <th>Percentage</th>
                <th>Categorie</th>
                <th>Opleidingen</th>
                <th className="text-transparent">&nbsp;.</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(c => {
                const rowTaskID = `${c.ID ?? c.id}`;
                const rowIsHighlighted = highlightedTaskID === rowTaskID;
                const highlightedCellStyle = rowIsHighlighted
                  ? { backgroundColor: "rgba(169, 169, 169, 0.85)", transition: "background-color 0.25s ease" }
                  : undefined;
                return (
                <tr
                  key={rowTaskID}
                  // Store row refs for diff navigation; hide rows when filtered; highlight jump target.
                  ref={el => {
                    if (el) {
                      taskRowRefs.current[rowTaskID] = el;
                    }
                  }}
                  className={c.programmes.filter(p => selectedProgrammeIDs.includes(p.ID)).length > 0 || selectedProgrammeIDs.length === 0 ? "" : "d-none"}>
                  <td style={highlightedCellStyle}>
                    {c.name}&nbsp;
                    {c.comment !== "" && (
                      <span className="text-info">
                        <i className="far fa-comment-dots" />
                      </span>
                    )}
                  </td>
                  <td style={highlightedCellStyle}>{c.percentage}</td>
                  <td style={highlightedCellStyle}>{c.category?.name}</td>
                  <td style={highlightedCellStyle}>
                    <ul>
                      {c.programmes.map(programme => (
                        <li key={programme.ID}>{programme.name}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="text-end" style={highlightedCellStyle}>
                    <Button variant="info" onClick={() => openEditModal(c)}>
                      <i className="far fa-edit" />
                    </Button>{" "}
                    <Button variant="danger" onClick={() => openRemoveModal(c)} disabled={snapshot}>
                      <i className="far fa-trash" />
                    </Button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}

export default Tasks;